/**
 * JSON Schema, as far as the playground reads the API's own document.
 *
 * The page runs under `script-src 'self'` with Trusted Types, so it cannot
 * use a validator that compiles schemas to code (Ajv's `new Function`). This
 * one walks the schema instead: `$ref` into the document, `allOf`/`oneOf`/
 * `anyOf`/`not`, the type, string, number, array and object keywords, the
 * formats the API uses, and `unevaluatedProperties: false` as the spec uses
 * it (to close an `allOf`). Unknown keywords are annotations and are ignored,
 * so a new keyword in the spec never breaks the page.
 *
 * It also makes examples: a value every hole's schema accepts, so a card the
 * spec dealt yesterday can be fired today without code knowing its shape.
 */

import type { Json } from '../contracts';
import { isObj, type JsonObject } from '../model/json';

export interface SchemaError { readonly path: string; readonly message: string }


/** Follow a `#/a/b` pointer into the document. */
export const pointerAt = (doc: Json, ref: string): Json | undefined => {
  if (!ref.startsWith('#')) return undefined;
  const parts = ref.slice(1).split('/').slice(1).map((p) => decodeURIComponent(p).replace(/~1/g, '/').replace(/~0/g, '~'));
  let node: Json | undefined = doc;
  for (const p of parts) {
    if (Array.isArray(node)) node = node[Number(p)];
    else if (isObj(node)) node = node[p];
    else return undefined;
  }
  return node;
};

/** `#/components/schemas/OccupierRef` → `OccupierRef`. */
export const refName = (ref: string): string => ref.slice(ref.lastIndexOf('/') + 1).replace(/~1/g, '/').replace(/~0/g, '~');

/** A schema with its own `$ref` chain followed (siblings of `$ref` win), not its children. */
export const deref = (doc: Json, schema: Json, depth = 0): Json => {
  if (!isObj(schema) || typeof schema.$ref !== 'string' || depth > 32) return schema;
  const { $ref, ...rest } = schema;
  const target = pointerAt(doc, $ref);
  if (!isObj(target)) return rest;
  return deref(doc, { ...target, ...rest }, depth + 1);
};

/** Every `$ref` replaced by what it points to, as MCP tool schemas are. The document has no cycles. */
export const inline = (doc: Json, value: Json, depth = 0): Json => {
  if (depth > 48) throw new Error('schema nests too deep to inline; is there a cycle?');
  if (Array.isArray(value)) return value.map((v) => inline(doc, v, depth + 1));
  if (isObj(value)) {
    if (typeof value.$ref === 'string' && value.$ref.startsWith('#/')) {
      const { $ref, ...rest } = value;
      const target = pointerAt(doc, $ref);
      return inline(doc, { ...(isObj(target) ? target : {}), ...rest }, depth + 1);
    }
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, inline(doc, v, depth + 1)]));
  }
  return value;
};

/**
 * An `allOf` of object schemas folded into one object schema: properties
 * joined (a property named twice keeps both, as an `allOf`), `required`
 * joined, closing keywords kept. Only the top level is merged; the parts
 * must already be inlined.
 */
export const mergeAllOf = (schema: Json): Json => {
  if (!isObj(schema) || !Array.isArray(schema.allOf)) return schema;
  const { allOf, ...rest } = schema;
  const parts = [...allOf.map(mergeAllOf), rest];
  const properties: JsonObject = {};
  const required: string[] = [];
  const merged: JsonObject = {};
  for (const part of parts) {
    if (!isObj(part)) continue;
    for (const [k, v] of Object.entries(part)) {
      if (k === 'properties' && isObj(v)) {
        for (const [name, s] of Object.entries(v)) {
          const had = properties[name];
          properties[name] = had === undefined ? s : { allOf: [had, s] };
        }
      } else if (k === 'required' && Array.isArray(v)) {
        for (const r of v) if (typeof r === 'string' && !required.includes(r)) required.push(r);
      } else if (k === 'additionalProperties' || k === 'unevaluatedProperties') {
        // Closed if any part closes it.
        if (v === false || merged[k] === undefined) merged[k] = v;
      } else if (merged[k] === undefined) {
        merged[k] = v;
      }
    }
  }
  if (Object.keys(properties).length) merged.properties = properties;
  if (required.length) merged.required = required;
  if (merged.type === undefined && (merged.properties || merged.required)) merged.type = 'object';
  return merged;
};

// -- validation -------------------------------------------------------------------------------

const equal = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => equal(x, b[i]));
  const ka = Object.keys(a as object), kb = Object.keys(b as object);
  return ka.length === kb.length && ka.every((k) => equal((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
};

const typeOf = (v: unknown): string =>
  v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v === 'number' ? (Number.isInteger(v) ? 'integer' : 'number') : typeof v;

const matchesType = (t: string, v: unknown): boolean => {
  const actual = typeOf(v);
  return t === actual || (t === 'number' && actual === 'integer');
};

const regexCache = new Map<string, RegExp | null>();
const regex = (pattern: string): RegExp | null => {
  if (regexCache.has(pattern)) return regexCache.get(pattern)!;
  let r: RegExp | null = null;
  try { r = new RegExp(pattern, 'u'); } catch {
    try { r = new RegExp(pattern); } catch { r = null; }
  }
  regexCache.set(pattern, r);
  return r;
};

const isDate = (s: string): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === s;
};

const FORMATS: Readonly<Record<string, (s: string) => boolean>> = {
  date: isDate,
  'date-time': (s) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/i.test(s) && isDate(s.slice(0, 10)),
  uri: (s) => { try { return /^[a-z][a-z0-9+.-]*:/i.test(s) && Boolean(new URL(s)); } catch { return false; } },
  email: (s) => /^[^\s@]+@[^\s@]+$/.test(s),
};

export interface ValidateOptions {
  /** The document `$ref`s resolve against. */
  readonly doc?: Json;
  /** Also parse strings marked `contentMediaType: application/json` and check `contentSchema`. */
  readonly content?: boolean;
}

/** What is wrong with `value` under `schema`; empty when it is valid. */
export const validate = (schema: Json, value: unknown, options: ValidateOptions = {}): SchemaError[] => {
  const errors: SchemaError[] = [];
  check(schema, value, '', options, errors, 0);
  return errors;
};

export const isValid = (schema: Json, value: unknown, options: ValidateOptions = {}): boolean => validate(schema, value, options).length === 0;

/** The property names a schema evaluates for this value, for `unevaluatedProperties`. */
const evaluated = (schema: Json, value: unknown, options: ValidateOptions, depth: number): Set<string> | 'all' => {
  const out = new Set<string>();
  if (!isObj(schema) || depth > 64) return out;
  const s = options.doc ? deref(options.doc, schema) : schema;
  if (!isObj(s)) return out;
  if (s.additionalProperties !== undefined || s.unevaluatedProperties !== undefined) return 'all';
  if (isObj(s.properties)) for (const k of Object.keys(s.properties)) out.add(k);
  if (isObj(s.patternProperties) && isObj(value)) {
    for (const p of Object.keys(s.patternProperties)) {
      const r = regex(p);
      for (const k of Object.keys(value)) if (r?.test(k)) out.add(k);
    }
  }
  const branches: Json[] = [
    ...(Array.isArray(s.allOf) ? s.allOf : []),
    ...(Array.isArray(s.anyOf) ? s.anyOf : []).filter((b) => isValid(b, value, options)),
    ...(Array.isArray(s.oneOf) ? s.oneOf : []).filter((b) => isValid(b, value, options)),
  ];
  for (const b of branches) {
    const e = evaluated(b, value, options, depth + 1);
    if (e === 'all') return 'all';
    for (const k of e) out.add(k);
  }
  return out;
};

function check(schema: Json, value: unknown, path: string, options: ValidateOptions, errors: SchemaError[], depth: number): void {
  if (schema === true || schema === undefined) return;
  if (schema === false) { errors.push({ path, message: 'no value is allowed here' }); return; }
  if (!isObj(schema)) return;
  if (depth > 64) { errors.push({ path, message: 'the schema nests too deep' }); return; }
  const fail = (message: string) => errors.push({ path, message });

  if (typeof schema.$ref === 'string') {
    const target = options.doc ? pointerAt(options.doc, schema.$ref) : undefined;
    if (target === undefined) { fail(`cannot resolve ${schema.$ref}`); return; }
    check(target, value, path, options, errors, depth + 1);
  }

  const t = schema.type;
  if (typeof t === 'string' && !matchesType(t, value)) { fail(`must be ${t}`); return; }
  if (Array.isArray(t) && !t.some((x) => typeof x === 'string' && matchesType(x, value))) { fail(`must be ${t.join(' or ')}`); return; }
  if ('const' in schema && !equal(schema.const, value)) fail(`must be ${JSON.stringify(schema.const)}`);
  if (Array.isArray(schema.enum) && !schema.enum.some((e) => equal(e, value))) fail(`must be one of ${schema.enum.map((e) => JSON.stringify(e)).join(', ')}`);

  if (typeof value === 'string') {
    const length = Array.from(value).length;
    if (typeof schema.minLength === 'number' && length < schema.minLength) fail(`must be at least ${schema.minLength} characters`);
    if (typeof schema.maxLength === 'number' && length > schema.maxLength) fail(`must be at most ${schema.maxLength} characters`);
    if (typeof schema.pattern === 'string') {
      const r = regex(schema.pattern);
      if (r && !r.test(value)) fail(`must match ${schema.pattern}`);
    }
    if (typeof schema.format === 'string' && FORMATS[schema.format] && !FORMATS[schema.format]!(value)) fail(`must be a ${schema.format}`);
    if (options.content && schema.contentMediaType === 'application/json') {
      let parsed: unknown;
      try { parsed = JSON.parse(value); } catch { fail('must be JSON text'); parsed = undefined; }
      if (parsed !== undefined && schema.contentSchema !== undefined) check(schema.contentSchema, parsed, path, options, errors, depth + 1);
    }
  }

  if (typeof value === 'number') {
    if (typeof schema.minimum === 'number' && value < schema.minimum) fail(`must be ≥ ${schema.minimum}`);
    if (typeof schema.maximum === 'number' && value > schema.maximum) fail(`must be ≤ ${schema.maximum}`);
    if (typeof schema.exclusiveMinimum === 'number' && value <= schema.exclusiveMinimum) fail(`must be > ${schema.exclusiveMinimum}`);
    if (typeof schema.exclusiveMaximum === 'number' && value >= schema.exclusiveMaximum) fail(`must be < ${schema.exclusiveMaximum}`);
    if (typeof schema.multipleOf === 'number' && schema.multipleOf > 0 && Math.abs(value / schema.multipleOf - Math.round(value / schema.multipleOf)) > 1e-9) fail(`must be a multiple of ${schema.multipleOf}`);
  }

  if (Array.isArray(value)) {
    if (typeof schema.minItems === 'number' && value.length < schema.minItems) fail(`must have at least ${schema.minItems} items`);
    if (typeof schema.maxItems === 'number' && value.length > schema.maxItems) fail(`must have at most ${schema.maxItems} items`);
    const prefix = Array.isArray(schema.prefixItems) ? schema.prefixItems : [];
    value.forEach((item, i) => {
      const s = i < prefix.length ? prefix[i] : schema.items;
      if (s !== undefined) check(s, item, `${path}/${i}`, options, errors, depth + 1);
    });
    if (schema.uniqueItems === true && value.some((x, i) => value.findIndex((y) => equal(x, y)) !== i)) fail('must not repeat an item');
  }

  if (isObj(value)) {
    const keys = Object.keys(value);
    if (Array.isArray(schema.required)) for (const r of schema.required) if (typeof r === 'string' && !(r in value)) errors.push({ path: `${path}/${r}`, message: 'is required' });
    if (typeof schema.minProperties === 'number' && keys.length < schema.minProperties) fail(`must have at least ${schema.minProperties} properties`);
    if (typeof schema.maxProperties === 'number' && keys.length > schema.maxProperties) fail(`must have at most ${schema.maxProperties} properties`);
    const props = isObj(schema.properties) ? schema.properties : {};
    const patterns = isObj(schema.patternProperties) ? Object.entries(schema.patternProperties) : [];
    for (const k of keys) {
      const at = `${path}/${k.replace(/~/g, '~0').replace(/\//g, '~1')}`;
      let known = false;
      if (k in props) { known = true; check(props[k]!, value[k], at, options, errors, depth + 1); }
      for (const [p, s] of patterns) if (regex(p)?.test(k)) { known = true; check(s, value[k], at, options, errors, depth + 1); }
      if (!known && schema.additionalProperties !== undefined) {
        if (schema.additionalProperties === false) errors.push({ path: at, message: 'is not allowed here' });
        else check(schema.additionalProperties, value[k], at, options, errors, depth + 1);
      }
    }
    if (schema.unevaluatedProperties !== undefined && schema.additionalProperties === undefined) {
      const seen = new Set<string>();
      for (const [k] of Object.entries(props)) seen.add(k);
      const open: JsonObject = { ...schema };
      delete open.unevaluatedProperties;
      const e = evaluated(open, value, options, depth);
      for (const k of keys) {
        if (e !== 'all' && !e.has(k) && !seen.has(k)) {
          const at = `${path}/${k}`;
          if (schema.unevaluatedProperties === false) errors.push({ path: at, message: 'is not allowed here' });
          else check(schema.unevaluatedProperties, value[k], at, options, errors, depth + 1);
        }
      }
    }
  }

  if (Array.isArray(schema.allOf)) for (const s of schema.allOf) check(s, value, path, options, errors, depth + 1);
  if (Array.isArray(schema.anyOf) && !schema.anyOf.some((s) => validate(s, value, options).length === 0)) fail('must match one of the allowed shapes');
  if (Array.isArray(schema.oneOf)) {
    const matched = schema.oneOf.filter((s) => validate(s, value, options).length === 0).length;
    if (matched !== 1) fail(matched === 0 ? 'must match one of the allowed shapes' : 'matches more than one allowed shape');
  }
  if (schema.not !== undefined && validate(schema.not, value, options).length === 0) fail('must not match the excluded shape');
}

// -- examples ---------------------------------------------------------------------------------

/** The far-future day drafts use: past days are refused, and this one is years off. */
export const DRAFT_DAY = '2031-05-15';
/** The occupier a draft names, by name: an id of 0 names no one, and names are what a hand types. */
export const DRAFT_OCCUPIER = 'alice';

const FILLERS = ['room', 'alice', 'a', '1', 'acme/room', 'x', '0'.repeat(32), '0'.repeat(16), '0'.repeat(8), '0'.repeat(6), '0'.repeat(4)];
const FORMAT_EXAMPLES: Readonly<Record<string, string>> = {
  date: DRAFT_DAY,
  'date-time': `${DRAFT_DAY}T09:00:00Z`,
  uri: 'https://hooks.example.com/sb',
  email: 'ada@example.com',
};

/** The literal a pattern must start with (`^wt_` → `wt_`), so a filler can follow it. */
const literalPrefix = (pattern: string): string => {
  if (!pattern.startsWith('^')) return '';
  let out = '';
  for (let i = 1; i < pattern.length; i++) {
    const c = pattern[i]!;
    if (c === '\\' && i + 1 < pattern.length && /[^\w]/.test(pattern[i + 1]!)) { out += pattern[++i]; continue; }
    if (/[\w\-/#@.:]/.test(c) && c !== '.') {
      // A literal only if it is not quantified.
      const next = pattern[i + 1];
      if (next === '*' || next === '?' || next === '{' || next === '+') break;
      out += c;
      continue;
    }
    break;
  }
  return out;
};

/**
 * The shortest string a simple pattern accepts: literals, escapes, classes
 * (their first member) and quantifiers (their least count). Null for
 * groups and alternations, which the fillers must cover instead.
 */
const fromPattern = (pattern: string): string | null => {
  const p = pattern.replace(/^\^/, '').replace(/\$$/, '');
  const escape = (e: string | undefined): string => (e === 'd' ? '0' : e === 'w' ? 'a' : e === 's' ? ' ' : e ?? '');
  let out = '';
  let i = 0;
  while (i < p.length) {
    const c = p[i]!;
    let unit: string;
    if (c === '\\') { unit = escape(p[i + 1]); i += 2; }
    else if (c === '[') {
      const end = p.indexOf(']', i + 2);
      if (end < 0) return null;
      const body = p.slice(i + 1, end);
      unit = body.startsWith('^') ? 'a' : body[0] === '\\' ? escape(body[1]) : body[0]!;
      i = end + 1;
    } else if ('()|'.includes(c)) return null;
    else { unit = c === '.' ? 'a' : c; i++; }
    const q = p[i];
    let times = 1;
    if (q === '*' || q === '?') { times = 0; i++; }
    else if (q === '+') i++;
    else if (q === '{') {
      const end = p.indexOf('}', i);
      if (end < 0) return null;
      times = Number(p.slice(i + 1, end).split(',')[0]);
      if (!Number.isInteger(times)) return null;
      i = end + 1;
    }
    out += unit.repeat(times);
  }
  return out;
};

const exampleString = (schema: JsonObject): string => {
  const ok = (s: string) => isValid(schema, s);
  const candidates: string[] = [];
  if (typeof schema.format === 'string' && FORMAT_EXAMPLES[schema.format]) candidates.push(FORMAT_EXAMPLES[schema.format]!);
  const prefix = typeof schema.pattern === 'string' ? literalPrefix(schema.pattern) : '';
  for (const f of FILLERS) candidates.push(prefix + f);
  for (const f of FILLERS) candidates.push(f);
  const shortest = typeof schema.pattern === 'string' ? fromPattern(schema.pattern) : null;
  if (shortest !== null) candidates.push(shortest);
  for (const c of candidates) {
    let s = c;
    if (typeof schema.maxLength === 'number') s = Array.from(s).slice(0, schema.maxLength).join('');
    if (typeof schema.minLength === 'number' && Array.from(s).length < schema.minLength) s = s.padEnd(schema.minLength, 'x');
    if (ok(s)) return s;
  }
  return candidates[0] ?? '';
};

/**
 * A value the schema accepts: its own example, default or const first, else
 * one built from its shape. Objects carry their required properties only;
 * a `oneOf` takes its first branch that yields a valid value.
 */
export const exampleOf = (schema: Json, depth = 0): Json => {
  if (!isObj(schema) || depth > 24) return null;
  if (Array.isArray(schema.examples) && schema.examples.length) return schema.examples[0]!;
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (schema.const !== undefined) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length) return schema.enum[0]!;
  if (Array.isArray(schema.allOf)) return exampleOf(mergeAllOf(schema), depth + 1);
  for (const key of ['oneOf', 'anyOf'] as const) {
    const branches = schema[key];
    if (Array.isArray(branches)) {
      for (const b of branches) {
        const v = exampleOf(b, depth + 1);
        if (isValid(schema, v)) return v;
      }
      return exampleOf(branches[0] ?? null, depth + 1);
    }
  }
  const types = Array.isArray(schema.type) ? schema.type : [schema.type];
  const t = types.find((x) => typeof x === 'string' && x !== 'null') ?? types[0] ?? (schema.properties ? 'object' : schema.items ? 'array' : undefined);
  switch (t) {
    case 'object': {
      const out: JsonObject = {};
      const props = isObj(schema.properties) ? schema.properties : {};
      const required = Array.isArray(schema.required) ? schema.required.filter((r): r is string => typeof r === 'string') : [];
      for (const r of required) out[r] = exampleOf(props[r] ?? {}, depth + 1);
      return out;
    }
    case 'array': {
      const n = Math.max(typeof schema.minItems === 'number' ? schema.minItems : 1, 0);
      const capped = typeof schema.maxItems === 'number' ? Math.min(n, schema.maxItems) : n;
      return Array.from({ length: capped }, () => exampleOf(schema.items ?? {}, depth + 1));
    }
    case 'integer':
    case 'number': {
      let v = typeof schema.minimum === 'number' ? schema.minimum : typeof schema.exclusiveMinimum === 'number' ? Math.floor(schema.exclusiveMinimum) + 1 : 0;
      if (typeof schema.maximum === 'number' && v > schema.maximum) v = schema.maximum;
      return v;
    }
    case 'boolean':
      return false;
    case 'string':
      return exampleString(schema);
    case 'null':
      return null;
    default:
      return null;
  }
};
